import { useCallback, useEffect, useState } from 'react';
import {
  Abas,
  Aviso,
  Botao,
  BotaoLink,
  CampoBusca,
  Carregando,
  Estatistica,
  Etiqueta,
  Icone,
  paginar,
  Paginacao,
  Selo,
  Vazio,
} from '../../componentes/ui/index.ts';
import { api, ErroApi, moeda, telefone } from '../../lib/api.ts';
import { exportarXlsx } from '../../lib/exportar.ts';
import type { LinhaFinanceira, ResumoFinanceiro } from '../../lib/tipos.ts';

const SITUACOES = [
  { valor: 'todos' as const, rotulo: 'Todos' },
  { valor: 'devedores' as const, rotulo: 'Em aberto' },
  { valor: 'quitados' as const, rotulo: 'Quitados' },
];

/**
 * Identifica pelo nome do adolescente — é como a liderança e as famílias se
 * referem à inscrição no dia a dia, com 40+ ativas o protocolo sozinho não
 * basta mais. Saúde e ficha completa continuam fora daqui, exclusivas do
 * `admin` — a API desta rota não devolve esses campos.
 */
export function Financeiro() {
  const [resumo, setResumo] = useState<ResumoFinanceiro | null>(null);
  const [linhas, setLinhas] = useState<LinhaFinanceira[]>([]);
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState<'todos' | 'devedores' | 'quitados'>('todos');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);

  // Trocar filtro ou busca volta para a primeira página.
  useEffect(() => setPagina(1), [busca, situacao]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = new URLSearchParams();
      if (busca.trim()) params.set('busca', busca.trim());
      if (situacao !== 'todos') params.set('situacao', situacao);

      const [r, l] = await Promise.all([
        api<ResumoFinanceiro>('/api/financeiro/resumo'),
        api<{ inscricoes: LinhaFinanceira[] }>(`/api/financeiro/inscricoes?${params}`),
      ]);
      setResumo(r);
      setLinhas(l.inscricoes);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao carregar');
    } finally {
      setCarregando(false);
    }
  }, [busca, situacao]);

  // Debounce na busca para não disparar uma query por tecla digitada.
  useEffect(() => {
    const t = setTimeout(() => void carregar(), 300);
    return () => clearTimeout(t);
  }, [carregar]);

  const visivel = paginar(linhas, pagina);

  // A planilha leva a lista inteira, não só a página que está na tela.
  function exportar() {
    exportarXlsx(
      'financeiro',
      linhas.map((l) => ({
        Protocolo: l.protocolo,
        Adolescente: l.nomeAdolescente,
        Responsável: l.nomeResponsavel ?? '',
        Telefone: l.telefoneResponsavel ?? '',
        'E-mail': l.emailResponsavel,
        Total: Number(l.valorTotal),
        Pago: Number(l.pago),
        Saldo: Number(l.saldo),
        Situação: l.quitado ? 'quitado' : 'em aberto',
        Enviada: l.enviadaEm ?? '',
      })),
    );
  }

  return (
    <div className="pilha-6">
      <div className="linha linha-entre">
        <div className="pilha-3">
          <Etiqueta>Financeiro</Etiqueta>
          <h2>Pagamentos</h2>
        </div>
        <Botao
          variante="secundario"
          tamanho="pequeno"
          disabled={linhas.length === 0}
          onClick={exportar}
          icone={<Icone.Download tamanho={14} />}
        >
          Exportar planilha
        </Botao>
      </div>

      {resumo && (
        <div className="grade">
          <Estatistica titulo="Inscrições" valor={String(resumo.inscricoes)} />
          {/* Os totais em dinheiro só existem na resposta da API para admin —
              o financeiro vê saldo inscrição por inscrição, nunca o total do
              acampamento. */}
          {resumo.previsto !== undefined && (
            <>
              <Estatistica titulo="Previsto" valor={moeda(resumo.previsto)} />
              <Estatistica titulo="Recebido" valor={moeda(resumo.recebido!)} tom="laranja" />
              <Estatistica
                titulo="A receber"
                valor={moeda(resumo.aReceber!)}
                tom={Number(resumo.aReceber) > 0 ? 'alerta' : 'sucesso'}
              />
            </>
          )}
        </div>
      )}

      <div className="linha">
        <CampoBusca
          valor={busca}
          aoMudar={setBusca}
          placeholder="Buscar por nome do adolescente ou código da inscrição"
          rotuloAcessivel="Buscar inscrições"
        />
        <Abas
          rotulo="Filtrar por situação"
          valor={situacao}
          aoMudar={setSituacao}
          opcoes={SITUACOES}
        />
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {carregando ? (
        <Carregando />
      ) : linhas.length === 0 ? (
        <Vazio titulo="Nenhuma inscrição encontrada">
          {busca ? 'Tente outro termo de busca.' : 'Ainda não há fichas enviadas para cobrar.'}
        </Vazio>
      ) : (
        <div className="tabela-caixa">
          <table className="tabela">
            <thead>
              <tr>
                <th>Adolescente</th>
                <th>Protocolo</th>
                <th>Responsável</th>
                <th className="num">Total</th>
                <th className="num">Pago</th>
                <th className="num">Saldo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visivel.itens.map((l) => (
                <tr key={l.id}>
                  <td style={{ fontWeight: 600 }}>{l.nomeAdolescente}</td>
                  <td className="mono texto-fraco">{l.protocolo}</td>
                  <td className="texto-fraco">
                    {l.nomeResponsavel ?? '—'}
                    <br />
                    {telefone(l.telefoneResponsavel)} · {l.emailResponsavel}
                  </td>
                  <td className="num mono">{moeda(l.valorTotal)}</td>
                  <td className="num mono">{moeda(l.pago)}</td>
                  <td className="num">
                    <Selo tom={l.quitado ? 'sucesso' : 'alerta'} ponto>
                      {l.quitado ? 'quitado' : moeda(l.saldo)}
                    </Selo>
                  </td>
                  <td>
                    <BotaoLink
                      para={`/painel/financeiro/${l.id}`}
                      variante="secundario"
                      tamanho="pequeno"
                    >
                      Lançar
                    </BotaoLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Paginacao total={linhas.length} pagina={visivel.pagina} aoMudar={setPagina} />
    </div>
  );
}
